class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-drawer-upsells')) {
  customElements.define(
    'cart-drawer-upsells',
    class CartDrawerUpsells extends HTMLElement {
      static recommendationCache = new Map();

      constructor() {
        super();
        this.abortController = null;
        this.requestToken = 0;
      }

      connectedCallback() {
        this.lines = this.readLines();
        this.cartProductIds = new Set(this.lines.map((line) => String(line.product_id)));
        this.list = this.querySelector('[data-cart-upsell-list]');
        this.status = this.querySelector('[data-cart-upsell-status]');

        if (!this.lines.length || !this.list) {
          this.hide();
          return;
        }

        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      readLines() {
        const script = this.querySelector('[data-cart-upsell-lines]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent).filter((line) => line.product_id);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      async loadRecommendations() {
        const token = ++this.requestToken;
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.list.innerHTML = '';
        this.hide();

        const seenProductIds = new Set(this.cartProductIds);
        const cards = [];

        for (const line of this.lines) {
          if (cards.length >= 2) break;

          const html = await this.fetchRecommendations(line.product_id, this.abortController.signal).catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
            return '';
          });

          if (token !== this.requestToken || this.abortController.signal.aborted) return;
          if (!html) continue;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const sourceCards = doc.querySelectorAll('[data-cart-upsell-card]');

          for (const sourceCard of sourceCards) {
            if (cards.length >= 2) break;

            const productId = sourceCard.dataset.productId;
            if (!productId || seenProductIds.has(String(productId))) continue;

            const card = sourceCard.cloneNode(true);
            if (!this.prepareCard(card, line)) continue;

            seenProductIds.add(String(productId));
            cards.push(card);
          }
        }

        if (token !== this.requestToken) return;

        if (!cards.length) {
          this.hide();
          return;
        }

        cards.forEach((card) => this.list.appendChild(card));
        this.hidden = false;
        this.classList.remove('is-empty');
      }

      async fetchRecommendations(productId, signal) {
        const cacheKey = String(productId);
        if (CartDrawerUpsells.recommendationCache.has(cacheKey)) {
          return CartDrawerUpsells.recommendationCache.get(cacheKey);
        }

        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'complementary');

        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const html = await response.text();
        CartDrawerUpsells.recommendationCache.set(cacheKey, html);
        return html;
      }

      prepareCard(card, sourceLine) {
        const variants = this.readCardVariants(card);
        const availableVariants = variants.filter((variant) => variant.available);
        const select = card.querySelector('[data-cart-upsell-select]');
        const button = card.querySelector('[data-cart-upsell-add]');

        if (!button || !availableVariants.length) return false;

        if (!select) {
          button.dataset.variantId = availableVariants[0].id;
          button.disabled = false;
        } else {
          this.preselectVariant(card, variants, sourceLine);
          select.addEventListener('change', (event) => {
            event.stopPropagation();
            this.handleVariantChange(card, variants);
          });
          this.handleVariantChange(card, variants);
        }

        button.addEventListener('click', () => this.addVariant(card));
        return true;
      }

      readCardVariants(card) {
        const script = card.querySelector('[data-cart-upsell-variants]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      preselectVariant(card, variants, sourceLine) {
        const select = card.querySelector('[data-cart-upsell-select]');
        const sourceSize = this.findSourceSize(sourceLine.options || []);
        if (!select || !sourceSize) return;

        const optionNames = this.readOptionNames(card);
        const sizeIndex = this.findSizeIndex(optionNames);
        if (sizeIndex === -1) return;

        const match =
          variants.find(
            (variant) => variant.available && this.normalizeSize(variant.options?.[sizeIndex]) === sourceSize
          ) || variants.find((variant) => variant.available && this.hasSharedSimpleSize(sourceSize, variant.options?.[sizeIndex]));
        if (match) select.value = String(match.id);
      }

      readOptionNames(card) {
        try {
          return JSON.parse(card.dataset.optionNames || '[]');
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      findSourceSize(options) {
        const sizeOption = options.find((option) => /size|age/i.test(option.name || ''));
        return sizeOption ? this.normalizeSize(sizeOption.value) : '';
      }

      findSizeIndex(optionNames) {
        return optionNames.findIndex((name) => /size|age/i.test(name || ''));
      }

      normalizeSize(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/^size\s+/i, '');
      }

      hasSharedSimpleSize(sourceSize, targetSize) {
        const sourceParts = this.getSimpleSizeParts(sourceSize);
        const targetParts = this.getSimpleSizeParts(targetSize);

        if (sourceParts.length !== 1 || !targetParts.length) return false;
        return targetParts.includes(sourceParts[0]);
      }

      getSimpleSizeParts(value) {
        const normalized = this.normalizeSize(value);
        if (!/^\d+(\s*\/\s*\d+)?$/.test(normalized)) return [];

        return normalized.split('/').map((part) => part.trim());
      }

      handleVariantChange(card, variants) {
        const select = card.querySelector('[data-cart-upsell-select]');
        const button = card.querySelector('[data-cart-upsell-add]');
        const price = card.querySelector('[data-cart-upsell-price]');
        const selectedOption = select?.selectedOptions?.[0];
        const variant = variants.find((item) => String(item.id) === select.value);

        this.clearError(card);

        if (!variant) {
          button.disabled = true;
          delete button.dataset.variantId;
          return;
        }

        button.dataset.variantId = variant.id;
        button.disabled = !variant.available;

        if (price && selectedOption?.dataset.price) {
          price.textContent = selectedOption.dataset.price;
        }
      }

      async addVariant(card) {
        const button = card.querySelector('[data-cart-upsell-add]');
        const variantId = button?.dataset.variantId;
        const title = card.querySelector('.cart-upsell-card__title')?.textContent.trim() || 'recommended product';

        if (!button || !variantId || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        this.clearError(card);
        this.announce(`Adding ${title}`);
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];
        const body = JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(routes.cart_add_url, { ...fetchConfig('javascript'), body });
          const parsedState = await response.json();

          if (parsedState.status) {
            this.showError(card, parsedState.description || parsedState.message || window.cartStrings.error);
            return;
          }

          this.announce(`Added ${title}`);

          if (cartDrawer) {
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-upsells',
              productVariantId: variantId,
              cartData: parsedState,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            CartPerformance.measure('add:paint-updated-sections', () => {
              cartDrawer.renderContents(parsedState);
            });
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          console.error(error);
          this.showError(card, window.cartStrings.error);
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      showError(card, message) {
        const error = card.querySelector('[data-cart-upsell-error]');
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
        this.announce(message);
      }

      clearError(card) {
        const error = card.querySelector('[data-cart-upsell-error]');
        if (!error) return;
        error.textContent = '';
        error.hidden = true;
      }

      hide() {
        this.hidden = true;
        this.classList.add('is-empty');
      }

      announce(message) {
        if (!this.status) return;
        this.status.textContent = message;
      }
    }
  );
}
